<template>
    <Views :loading="loading" :error="error">
        <div class="d-flex">
            <v-text-field v-model="search" label="search" clearable dense hide-details prepend-inner-icon="mdi-magnify" outlined class="search" />
        </div>
        <v-data-table
            sort-by="name"
            must-sort
            dense
            class="table"
            mobile-breakpoint="0"
            :items-per-page="100"
            :items="nodes_"
            item-key="key"
            no-data-text="No nodes found"
            :headers="headers"
            :footer-props="{ itemsPerPageOptions: [5, 10, 20, 50, 100, -1] }"
        >
            <template #item.name="{ item }">
                <div class="d-flex align-center">
                    <router-link
                        :to="{ name: 'overview', params: { view: 'nodes', id: item.cluster_id + ':' + item.name, query: $utils.contextQuery() } }"
                        class="truncated"
                        style="max-width: 30ch"
                    >
                        {{ item.display_name || item.name }}
                    </router-link>
                    <v-btn icon x-small class="ml-1" title="Rename node" @click.stop="openRename(item)">
                        <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                </div>
            </template>

            <template #item.cluster_name="{ item }">
                <span class="truncated grey--text" style="max-width: 20ch">{{ item.cluster_name }}</span>
            </template>

            <template #item.os="{ item }">
                <div class="d-flex align-center">
                    <AppIcon :icon="item.os" />
                    <span v-if="item.kernel_version" class="caption grey--text ml-1 truncated" style="max-width: 50ch" :title="item.kernel_version">
                        {{ item.kernel_version }}
                    </span>
                </div>
            </template>

            <template #item.status="{ item }">
                <Led :status="item.status.status" />
                {{ item.status.message }}
                <span v-if="item.uptime_ms" class="caption grey--text"> ({{ $format.durationPretty(item.uptime_ms) }})</span>
            </template>

            <template #item.compute="{ item }">
                {{ item.compute }}
                <span v-if="item.instance_type" class="caption grey--text" :class="{ truncated: item.compute }"> ({{ item.instance_type }})</span>
            </template>

            <template #item.gpus="{ item }">
                <span v-if="item.gpus">{{ item.gpus }}</span>
                <span v-else>-</span>
            </template>

            <template #item.ips="{ item }">
                <v-menu v-if="item.ips.length > 1" offset-y tile>
                    <template #activator="{ on }">
                        <span v-on="on" class="text-no-wrap"> {{ item.ips[0] }}, ...</span>
                    </template>
                    <v-list dense>
                        <v-list-item v-for="v in item.ips" style="font-size: 14px; min-height: 32px">
                            <v-list-item-title>{{ v }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <span v-else>{{ item.ips[0] }}</span>
            </template>

            <template #item.cpu_percent="{ item }">
                <v-progress-linear
                    v-if="item.cpu_percent != null"
                    background-color="blue lighten-3"
                    height="16"
                    color="blue lighten-1"
                    :value="item.cpu_percent"
                    style="min-width: 64px"
                >
                    <span style="font-size: 14px">{{ item.cpu_percent }}%</span>
                </v-progress-linear>
            </template>
            <template #item.memory_percent="{ item }">
                <v-progress-linear
                    v-if="item.memory_percent != null"
                    background-color="purple lighten-3"
                    height="16"
                    color="purple lighten-1"
                    :value="item.memory_percent"
                    style="min-width: 64px"
                >
                    <span style="font-size: 14px">{{ item.memory_percent }}%</span>
                </v-progress-linear>
            </template>
            <template #item.disk_io_percent="{ item }">
                <v-progress-linear
                    v-if="item.disk_io_percent != null"
                    background-color="orange lighten-3"
                    height="16"
                    color="orange lighten-1"
                    :value="item.disk_io_percent"
                    style="min-width: 64px"
                >
                    <span style="font-size: 14px">{{ item.disk_io_percent }}%</span>
                </v-progress-linear>
                <span v-else class="grey--text">-</span>
            </template>
            <template #item.total_network_bandwidth="{ item }">
                <template v-if="item.network_bandwidth">
                    <span class="text-no-wrap">
                        <v-icon small color="green">mdi-arrow-down-thick</v-icon>{{ $format.formatBandwidth(item.network_bandwidth.rx) }}
                    </span>
                    <span class="text-no-wrap">
                        <v-icon small color="blue">mdi-arrow-up-thick</v-icon>{{ $format.formatBandwidth(item.network_bandwidth.tx) }}
                    </span>
                </template>
                {{ item.network }}
            </template>
        </v-data-table>

        <v-dialog v-model="renameDialog" max-width="440">
            <v-card>
                <v-card-title>Rename node</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="renameValue"
                        label="Display name"
                        maxlength="100"
                        counter="100"
                        autofocus
                        @keyup.enter="saveRename"
                    />
                    <v-alert v-if="renameError" type="error" dense text class="mb-0">{{ renameError }}</v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="renameDialog = false">Cancel</v-btn>
                    <v-spacer />
                    <v-btn v-if="renameNode && renameNode.display_name" text :disabled="savingRename" @click="resetRename">Reset</v-btn>
                    <v-btn color="primary" :loading="savingRename" @click="saveRename">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div class="mt-4">
            <AgentInstallation color="primary">Add nodes</AgentInstallation>
        </div>
    </Views>
</template>

<script>
import Views from '@/views/Views.vue';
import AgentInstallation from '@/views/AgentInstallation.vue';
import Led from '@/components/Led.vue';
import AppIcon from '@/components/AppIcon.vue';

export default {
    components: { Led, Views, AgentInstallation, AppIcon },

    data() {
        return {
            nodes: [],
            loading: false,
            error: '',
            search: '',
            renameDialog: false,
            renameNode: null,
            renameValue: '',
            renameError: '',
            savingRename: false,
        };
    },

    computed: {
        headers() {
            let headers = [
                { value: 'name', text: 'Name', align: 'left' },
                { value: 'status', text: 'Status', align: 'left' },
                { value: 'compute', text: 'Compute', align: 'left', sortable: false },
                { value: 'ips', text: 'IP', align: 'left', sortable: false },
                { value: 'cpu_percent', text: 'CPU', align: 'left' },
                { value: 'memory_percent', text: 'Memory', align: 'left' },
                { value: 'disk_io_percent', text: 'Disk I/O', align: 'left', sortable: false },
                { value: 'gpus', text: 'GPU', align: 'left' },
                { value: 'total_network_bandwidth', text: 'Network', align: 'left' },
                { value: 'os', text: 'OS', align: 'left' },
                { value: 'cluster_name', text: 'Cluster', align: 'left' },
            ];
            if (!this.$api.context.multicluster) {
                return headers.filter((h) => h.value !== 'cluster_name');
            }
            return headers;
        },
        nodes_() {
            const nodes = (this.nodes || []).map((n) => ({ ...n, key: n.cluster_id + ':' + n.name }));
            if (!this.search) {
                return nodes;
            }
            return nodes.filter((n) =>
                (n.name + ' ' + n.display_name + ' ' + n.cluster_name + ' ' + n.ips.join(' ')).toLowerCase().includes(this.search.toLowerCase()),
            );
        },
    },

    mounted() {
        this.get();
        this.$events.watch(this, this.get, 'refresh');
    },

    methods: {
        get() {
            this.loading = true;
            this.error = '';
            this.$api.getOverview('nodes', '', (data, error) => {
                this.loading = false;
                if (error) {
                    this.error = error;
                    return;
                }
                this.nodes = data.nodes;
            });
        },
        openRename(item) {
            this.renameNode = item;
            this.renameValue = item.display_name || item.name;
            this.renameError = '';
            this.renameDialog = true;
        },
        saveRename() {
            this.persistRename(this.renameValue.trim());
        },
        resetRename() {
            this.persistRename('');
        },
        persistRename(displayName) {
            if (!this.renameNode || this.savingRename) {
                return;
            }
            this.savingRename = true;
            this.renameError = '';
            this.$api.saveNodeDisplayName(
                {
                    cluster_id: this.renameNode.cluster_id,
                    name: this.renameNode.name,
                    display_name: displayName,
                },
                (data, error) => {
                    this.savingRename = false;
                    if (error) {
                        this.renameError = error;
                        return;
                    }
                    this.renameDialog = false;
                    this.get();
                },
            );
        },
    },
};
</script>

<style scoped>
.table:deep(table) {
    min-width: 500px;
}
.table:deep(tr:hover) {
    background-color: unset !important;
}
.table:deep(th),
.table:deep(td) {
    padding: 4px 8px !important;
}
.table:deep(th) {
    white-space: nowrap;
}
.search {
    max-width: 200px !important;
}
.truncated {
    max-width: 15ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: bottom;
}
</style>
